import React, { useCallback, useEffect, useReducer, useRef, useState } from "react"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { getIdEstabelecimentoAtual } from "../../../services/quitacaoAutomatica"
import {
    associarEntregadorAoPedido,
    buscarPedidosParaRoteirizar,
    jaRoteirizado,
    listarEntregadoresAtivos,
    obterUsoRoteirizacao,
    salvarUsoRoteirizacao,
} from "../../../services/roteirizacao"
import { Loading } from "../../RegistradorDePedidos"
import {
    CHAVE_FILTRO_STATUS,
    RoteirizacaoInicial,
    type ProvedorMapa,
} from "../models/roteirizacaoModel"
import { RoteirizacaoActionsTypes } from "./roteirizacaoActions"
import { RoteirizacaoContext, type ResultadoAssociacao } from "./RoteirizacaoContext"
import { roteirizacaoReducer } from "./roteirizacaoReducer"

/** Loja selecionada no seletor multi-loja (localStorage), quando houver. */
function lerEstabSelecionado(): number | string | null {
    try {
        const estab = JSON.parse(localStorage.getItem("estabelecimentoAtualCF") || "null")
        return estab && estab.id ? estab.id : null
    } catch {
        return null
    }
}

export function RoteirizacaoContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(roteirizacaoReducer, RoteirizacaoInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const configuracoes = useAppStore(s => s.configuracoes)
    const parametros = configuracoes?.user?.parametros

    const autorizacao = localStorage.getItem("chaveSistema") ?? ""
    // Prefere a loja selecionada (multi-loja); senão o estabelecimento do usuário
    // logado (mesma fonte da tela Pedidos do Dia); por último o fallback legado.
    const idEstabelecimento =
        lerEstabSelecionado() ?? parametros?.estabelecimento?.id ?? getIdEstabelecimentoAtual()

    // Provedor de mapa conforme a configuração do cliente (tipoSelecaoEndereco):
    // 2 = MapBox (se houver token), caso contrário Google Maps (já carregado no App).
    const tokenMapbox: string = parametros?.tokenMapBox ?? ""
    const provedorMapa: ProvedorMapa =
        parametros?.tipoSelecaoEndereco === 2 && tokenMapbox ? "mapbox" : "google"

    const latOrigem = Number(parametros?.estabelecimento?.latitude)
    const lngOrigem = Number(parametros?.estabelecimento?.longitude)
    const origem =
        Number.isFinite(latOrigem) && Number.isFinite(lngOrigem) && latOrigem !== 0 && lngOrigem !== 0
            ? { lat: latOrigem, lng: lngOrigem }
            : null

    // Ref com o estado mais recente para uso em callbacks assíncronas.
    const stateRef = useRef(dados)
    stateRef.current = dados

    const carregar = useCallback(
        async (dataConsulta: string) => {
            if (!autorizacao) {
                setErroCarregamento("Sessão inválida. Faça login novamente.")
                return
            }
            if (!idEstabelecimento) {
                setErroCarregamento("Estabelecimento não identificado.")
                return
            }
            setLoading(true)
            setErroCarregamento(null)
            try {
                const [pedidosResp, entregadoresResp] = await Promise.all([
                    buscarPedidosParaRoteirizar(
                        autorizacao,
                        idEstabelecimento,
                        dataConsulta,
                        stateRef.current.filtroStatus
                    ),
                    listarEntregadoresAtivos(autorizacao),
                ])

                let erroMsg: string | null = null

                let pedidos = stateRef.current.pedidos
                if (Array.isArray(pedidosResp)) {
                    pedidos = pedidosResp
                } else {
                    pedidos = []
                    erroMsg = pedidosResp.mensagem || "Erro ao carregar pedidos."
                }

                let entregadores = stateRef.current.entregadores
                if (Array.isArray(entregadoresResp)) {
                    entregadores = entregadoresResp
                } else {
                    entregadores = []
                    erroMsg = erroMsg ?? (entregadoresResp.mensagem || "Erro ao carregar entregadores.")
                }

                dispatch({
                    type: RoteirizacaoActionsTypes.CARREGAR_DADOS,
                    payload: { pedidos, entregadores },
                })

                if (idEstabelecimento) {
                    const uso = await obterUsoRoteirizacao(idEstabelecimento, autorizacao)
                    if (!("retornoErro" in uso)) {
                        dispatch({ type: RoteirizacaoActionsTypes.SET_USO, payload: uso })
                    }
                }

                if (erroMsg) setErroCarregamento(erroMsg)
            } catch (error) {
                setErroCarregamento("Erro ao carregar os dados da roteirização.")
            } finally {
                setLoading(false)
            }
        },
        [autorizacao, idEstabelecimento, setLoading]
    )

    // Carga inicial e ao mudar o filtro de status. Trocas de data só recarregam
    // via "Atualizar" (recarregar), para não disparar uma busca a cada tecla
    // digitada na data.
    useEffect(() => {
        carregar(stateRef.current.data)
    }, [carregar, dados.filtroStatus])

    // Mantém o último filtro salvo localmente, para persistir ao trocar de tela.
    useEffect(() => {
        try {
            localStorage.setItem(CHAVE_FILTRO_STATUS, JSON.stringify(dados.filtroStatus))
        } catch {
            /* ignore */
        }
    }, [dados.filtroStatus])

    const recarregar = useCallback(() => carregar(stateRef.current.data), [carregar])

    const associarSelecionados = useCallback(async (): Promise<ResultadoAssociacao> => {
        const estado = stateRef.current
        const vazio: ResultadoAssociacao = {
            associados: 0,
            novosRoteirizados: 0,
            bloqueadosPorLimite: 0,
            limiteAtingido: false,
        }

        const entregadorId = estado.entregadorSelecionadoId
        if (!entregadorId) {
            return { ...vazio, erro: "Selecione um entregador." }
        }
        if (!autorizacao || !idEstabelecimento) {
            return { ...vazio, erro: "Sessão inválida. Faça login novamente." }
        }

        const selecionados = estado.pedidos.filter(p => estado.pedidosSelecionados.includes(p.id))
        if (selecionados.length === 0) {
            return { ...vazio, erro: "Selecione ao menos um pedido." }
        }

        const entregador = estado.entregadores.find(e => e.id === entregadorId)
        const nomeEntregador = entregador ? entregador.nome : "Entregador"

        const novos = selecionados.filter(p => !jaRoteirizado(p))
        const reatribuir = selecionados.filter(p => jaRoteirizado(p))

        // Leitura fresca do contador reduz a corrida entre estações abertas em paralelo.
        let usados = estado.usados
        let limite = estado.limite
        let ilimitado = estado.ilimitado
        let idParam = estado.idParametroContador
        const uso = await obterUsoRoteirizacao(idEstabelecimento, autorizacao)
        if (!("retornoErro" in uso)) {
            usados = uso.usados
            limite = uso.limite
            ilimitado = uso.ilimitado
            idParam = uso.idParametroContador
            dispatch({
                type: RoteirizacaoActionsTypes.SET_USO,
                payload: { usados, limite, ilimitado, idParametroContador: idParam },
            })
        }

        // Com o recurso ROTEIRIZACAO ativo na assinatura, não há teto: todos os novos passam.
        const restantes = ilimitado ? novos.length : Math.max(0, limite - usados)
        const novosPermitidos = novos.slice(0, restantes)
        const novosBloqueados = novos.slice(restantes)

        // Reatribuir (troca de entregador) não consome cota; novos permitidos consomem.
        const aProcessar = [...reatribuir, ...novosPermitidos]

        const idsAssociados: number[] = []
        if (aProcessar.length > 0) {
            setLoading(true)
            try {
                for (const pedido of aProcessar) {
                    const resultado = await associarEntregadorAoPedido(
                        pedido.id,
                        entregadorId,
                        autorizacao
                    )
                    if (resultado === true) idsAssociados.push(pedido.id)
                }
            } finally {
                setLoading(false)
            }
        }

        const novosRoteirizados = novosPermitidos.filter(p => idsAssociados.includes(p.id)).length
        let novoTotal = usados
        if (novosRoteirizados > 0) {
            novoTotal = usados + novosRoteirizados
            const salvo = await salvarUsoRoteirizacao(
                idEstabelecimento,
                idParam,
                novoTotal,
                autorizacao
            )
            if (!("retornoErro" in salvo)) {
                idParam = salvo.idParametroContador
            }
        }

        dispatch({
            type: RoteirizacaoActionsTypes.APLICAR_ASSOCIACAO,
            payload: {
                ids: idsAssociados,
                nomeEntregador,
                usados: novoTotal,
                idParametroContador: idParam,
            },
        })

        return {
            associados: idsAssociados.length,
            novosRoteirizados,
            bloqueadosPorLimite: novosBloqueados.length,
            limiteAtingido: novosBloqueados.length > 0,
        }
    }, [autorizacao, idEstabelecimento, setLoading])

    return (
        <RoteirizacaoContext.Provider
            value={{
                dados,
                dispatch,
                erroCarregamento,
                provedorMapa,
                origem,
                tokenMapbox,
                recarregar,
                associarSelecionados,
            }}
        >
            <Loading />
            {children}
        </RoteirizacaoContext.Provider>
    )
}
